import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VoiceCustomerCandidate, VoiceProposal } from '@worki/shared';
import { useToast } from '../components/Toast.js';
import { applyEdits, fieldValues, rowFromItem, voiceApi, type VoiceFlag } from './voice.js';

type Line = { key: string; description: string; quantity: number; unitPrice: number };
type Person = { name: string; phone: string | null; email: string | null; city: string | null };

/**
 * VOICE-001 — what a voice note does to the quote editor, kept out of the
 * page so the page only says where things go on screen.
 *
 * Nothing in here saves. A proposal fills the form the way a person typing
 * would, and the owner still reads it and presses save; that is the whole
 * promise of the feature, so every path below ends at the form's own state.
 *
 * The rows a voice note wrote are remembered by their React key with what to
 * outline them with: a price that came from history, a price nobody said, or
 * an amount that did not add up. The mark goes the moment the owner touches
 * the row — after that the number is theirs, not the note's.
 */
export function useVoiceProposal<Row extends Line>({
  editing,
  jobId,
  items,
  setItems,
  newItem,
  filled,
  setValue,
  values,
}: {
  editing: boolean;
  /** `?voiceJob=` on a new quote: a note recorded somewhere else, opened here. */
  jobId: string | null;
  items: Row[];
  setItems: (rows: Row[]) => void;
  newItem: () => Row;
  filled: (row: Row) => boolean;
  setValue: (name: string, value: unknown) => void;
  /** The form as it is now: what a spoken field is added to, or merged over. */
  values: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  /** Rows a voice note filled in, by React key, and how to mark them. */
  const [flags, setFlags] = useState<Map<string, VoiceFlag | 'touched'>>(new Map());
  /** A spoken name that matched more than one stored customer. */
  const [candidates, setCandidates] = useState<VoiceCustomerCandidate[] | null>(null);
  /** Details the note said, kept over a candidate picked after it. */
  const spokenDetails = useRef<Record<string, string>>({});

  /*
   * The editor never pins a quote to a stored customer record (`customerId` is
   * always sent empty — see `submit` in the page). A stored customer is found
   * again by the phone in the details, through `CustomerMatchNotice`. So a
   * matched customer, spoken or picked, is filled in as if typed, and the
   * notice then asks exactly what it asks for a typed one.
   */
  const customerOf = (c: Person) => ({ name: c.name, phone: c.phone ?? '', email: c.email ?? '', city: c.city ?? '' });

  /*
   * 04.10 — everything else the note said: the customer's details, a discount,
   * VAT exemption, validity, follow-up, the work address and a note. `customer`
   * is what the form holds by the time these land — on a new quote, the card
   * the spoken name matched, which a setValue a moment ago has not reached
   * `values` yet.
   */
  const applyFields = (proposal: VoiceProposal, customer: Record<string, string>) => {
    const pairs = fieldValues(
      proposal.fields,
      { customer, legalText: String(values.legalText ?? '') },
      (address) => t('voice.workAddressLine', { address }),
    );
    for (const [name, value] of pairs) setValue(name, value);
  };
  const currentCustomer = (): Record<string, string> => {
    const c = (values.customer ?? {}) as Record<string, unknown>;
    return { name: String(c.name ?? ''), phone: String(c.phone ?? ''), email: String(c.email ?? ''), city: String(c.city ?? '') };
  };

  /*
   * A new quote filled in from a voice note. Same rule as the mini-site
   * prefill in the page: a starting point, applied once, the owner's to change.
   */
  const applyCreateProposal = (proposal: VoiceProposal) => {
    if (proposal.title) setValue('title', proposal.title);
    if (proposal.pricesIncludeVat !== null) setValue('pricesIncludeVat', proposal.pricesIncludeVat);
    const spoken = proposal.customer;
    let customer = currentCustomer();
    if (spoken.match === 'exact') customer = customerOf(spoken.customer);
    if (spoken.match === 'none' && spoken.spokenName) {
      customer = customerOf({ name: spoken.spokenName, phone: null, email: null, city: null });
    }
    if (spoken.match === 'candidates') {
      // The spoken name goes in straight away, so dismissing the picker leaves
      // a new customer by that name rather than an empty field.
      customer = customerOf({ name: spoken.spokenName, phone: null, email: null, city: null });
      setCandidates(spoken.candidates);
    }
    // The spoken details sit over the card's: said now, so newer.
    spokenDetails.current = { ...proposal.fields?.customer };
    setValue('customer', { ...customer, ...spokenDetails.current });
    applyFields({ ...proposal, fields: { ...proposal.fields, customer: undefined } }, customer);
    const rows = proposal.items.map((item) => ({ ...newItem(), ...rowFromItem(item) }));
    if (rows.length > 0) {
      setItems(rows.map(({ flag: _flag, ...row }) => row as unknown as Row));
      setFlags(new Map(rows.filter((r) => r.flag).map((r) => [r.key, r.flag!])));
    }
  };

  /*
   * Spoken changes to a draft. The positions the server returns index the
   * snapshot the job was sent with, which is `items.filter(filled)` — so the
   * edits are applied to that same filtered list, never to the raw rows. A
   * blank trailing row is dropped by this on purpose: it was not part of what
   * the note talked about, and an empty row is one tap to add back.
   */
  const applyEditProposal = (proposal: VoiceProposal) => {
    const result = applyEdits(items.filter(filled), proposal.edits);
    const rows = result.rows.map((row) => ({ ...newItem(), ...row }));
    // Never leave the form with no row at all, as the bin above the rows does.
    setItems(rows.length > 0 ? rows.map(({ flag: _flag, ...row }) => row as unknown as Row) : [newItem()]);
    setFlags(
      new Map(
        rows.flatMap((row, i): Array<[string, VoiceFlag | 'touched']> =>
          row.flag ? [[row.key, row.flag]] : result.touched.includes(i) ? [[row.key, 'touched']] : [],
        ),
      ),
    );
    if (proposal.pricesIncludeVat !== null) setValue('pricesIncludeVat', proposal.pricesIncludeVat);
    if (proposal.title) setValue('title', proposal.title);
    applyFields(proposal, currentCustomer());
    // "Not saved yet" either way: the toast is the only sign the rows moved
    // while the owner was looking at the dialog.
    if (result.skipped > 0) toast.show(t('voice.editsSkipped', { count: result.skipped }));
    else toast.success(t('voice.editsApplied'));
  };

  useEffect(() => {
    if (editing || !jobId) return;
    let gone = false;
    voiceApi
      .get(jobId)
      .then((job) => {
        if (!gone && job.status === 'ready' && job.proposal) applyCreateProposal(job.proposal);
      })
      // A job that cannot be read (expired, someone else's) opens an empty new
      // quote, which is what the address would show without the parameter.
      .catch(() => undefined);
    return () => {
      gone = true;
    };
    // Mount only, like the mini-site prefill: a later render must not
    // overwrite what the owner has typed since.
  }, []);

  return {
    flags,
    /** Called from the page's row update: an edited row is no longer the note's. */
    clearFlag: (key: string) =>
      setFlags((current) => {
        if (!current.has(key)) return current;
        const next = new Map(current);
        next.delete(key);
        return next;
      }),
    candidates,
    pickCandidate: (c: VoiceCustomerCandidate) => {
      setValue('customer', { ...customerOf(c), ...spokenDetails.current });
      setCandidates(null);
    },
    dismissCandidates: () => setCandidates(null),
    applyCreateProposal,
    applyEditProposal,
  };
}
